import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Joyride, { ACTIONS, EVENTS, STATUS, type CallBackProps, type TooltipRenderProps } from "react-joyride";
import { useNavigate, useLocation } from "react-router";
import * as Sentry from "@sentry/react";
import { useTour } from "@providers/TourProvider";
import ScoutTooltip from "./ScoutTooltip";
import ConfirmModal from "./ConfirmModal";
import { isTargetedStep, stepTarget } from "@/tours/types";
import { isOnRoute } from "@/tours/tour-utils";
import type { TourStep, ScoutState } from "@/tours/types";

const SPOTLIGHT_PADDING = 4;

const OVERLAY_COLOR = "rgba(60, 76, 102, 0.7)";
const TOUR_KEYFRAMES = `
@keyframes tour-spotlight-pulse {
  0% { box-shadow: 0 0 0 9999px ${OVERLAY_COLOR}, 0 0 0 0 rgba(46, 222, 229, 0.4); }
  50% { box-shadow: 0 0 0 9999px ${OVERLAY_COLOR}, 0 0 0 10px rgba(46, 222, 229, 0); }
  100% { box-shadow: 0 0 0 9999px ${OVERLAY_COLOR}, 0 0 0 0 rgba(46, 222, 229, 0.4); }
}
@keyframes tour-spinner {
  to { transform: rotate(360deg); }
}
`;

function TourOverlay({
    target,
    isCenter,
    spotlightClicks,
    isAction,
    measureKey,
    disableAutoScroll,
    isWaiting,
    onOverlayClick,
}: {
    target: string;
    isCenter: boolean;
    spotlightClicks: boolean;
    isAction: boolean;
    measureKey: number;
    disableAutoScroll?: boolean;
    isWaiting?: boolean;
    onOverlayClick?: () => void;
}) {
    const [rect, setRect] = useState<DOMRect | null>(null);

    useLayoutEffect(() => {
        if (isCenter) {
            setRect(null);
            return;
        }

        const el = document.querySelector(target);
        if (!el) {
            setRect(null);
            return;
        }

        // Scroll target into view if it's off-screen
        const elRect = el.getBoundingClientRect();
        let scrollTimeoutId: ReturnType<typeof setTimeout> | undefined;
        let unmounted = false;
        if (!disableAutoScroll && (elRect.top < 0 || elRect.bottom > window.innerHeight)) {
            // Temporarily allow scroll, then re-lock after smooth scroll completes.
            // 500ms is a conservative estimate of smooth scroll duration; if it takes longer
            // on slow devices, overflow re-locks mid-scroll (acceptable — spotlight still repositions).
            const html = document.documentElement;
            html.style.overflow = "";
            try {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
            } catch (err) {
                console.warn("[Tours] scrollIntoView failed for target:", target, err);
                html.style.overflow = "hidden"; // Re-lock immediately on failure
            }
            scrollTimeoutId = setTimeout(() => {
                if (!unmounted) {
                    html.style.overflow = "hidden";
                }
            }, 500);
        }

        let lastTop = 0;
        let lastLeft = 0;
        let dispatchingResize = false;
        /** Core position update. `nudgeFloater` controls whether to dispatch a resize event
         *  to reposition Joyride's floater. ResizeObserver callbacks pass false to avoid a
         *  feedback loop (resize dispatch → resize listener → update → resize dispatch…). */
        const update = (nudgeFloater = true) => {
            const r = el.getBoundingClientRect();
            // Detached elements return a zero-rect — clear spotlight to avoid jumping to (0,0)
            if (r.width === 0 && r.height === 0 && !el.isConnected) {
                setRect(null);
                return;
            }
            setRect(r);
            if (nudgeFloater && (r.top !== lastTop || r.left !== lastLeft) && !dispatchingResize) {
                lastTop = r.top;
                lastLeft = r.left;
                dispatchingResize = true;
                window.dispatchEvent(new Event("resize"));
                dispatchingResize = false;
            }
        };
        update();

        const ro = new ResizeObserver(() => update(false));
        ro.observe(el);
        const onScrollOrResize = () => update();
        window.addEventListener("scroll", onScrollOrResize, { passive: true });
        window.addEventListener("resize", onScrollOrResize, { passive: true });
        // Poll position to catch sibling-induced layout shifts (e.g. content loading above the target)
        const positionPoll = setInterval(() => update(), 200);

        return () => {
            unmounted = true;
            if (scrollTimeoutId) clearTimeout(scrollTimeoutId);
            ro.disconnect();
            clearInterval(positionPoll);
            window.removeEventListener("scroll", onScrollOrResize);
            window.removeEventListener("resize", onScrollOrResize);
        };
    }, [target, isCenter, measureKey, disableAutoScroll]);

    // Full overlay for center steps or when target not found
    if (isCenter || !rect) {
        return (
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- modal backdrop overlay; click-outside-to-dismiss is not a button interaction
            <div
                style={{
                    position: "fixed",
                    inset: 0,
                    backgroundColor: OVERLAY_COLOR,
                    zIndex: 10000,
                }}
                onClick={onOverlayClick}
            >
                {isWaiting && (
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                            gap: 12,
                        }}
                    >
                        <div
                            style={{
                                width: 32,
                                height: 32,
                                border: "4px solid rgba(255,255,255,0.2)",
                                borderTopColor: "#2EDEE5",
                                borderRadius: "50%",
                                animation: "tour-spinner 0.8s linear infinite",
                            }}
                        />
                        <p
                            style={{
                                color: "rgba(255,255,255,0.7)",
                                fontSize: 14,
                                fontFamily: '"Plus Jakarta Sans", sans-serif',
                                margin: 0,
                            }}
                        >
                            Waiting for the next step…
                        </p>
                    </div>
                )}
            </div>
        );
    }

    // Clamp spotlight to visible viewport so tall elements don't push the tooltip off-screen
    const clampedTop = Math.max(0, rect.top - SPOTLIGHT_PADDING);
    const clampedBottom = Math.min(window.innerHeight, rect.bottom + SPOTLIGHT_PADDING);
    const clampedLeft = Math.max(0, rect.left - SPOTLIGHT_PADDING);
    const clampedRight = Math.min(window.innerWidth, rect.right + SPOTLIGHT_PADDING);
    const clampedWidth = clampedRight - clampedLeft;
    const clampedHeight = clampedBottom - clampedTop;

    // If element is entirely off-screen (e.g., scrollIntoView hasn't completed), render full overlay
    if (clampedHeight <= 0 || clampedWidth <= 0) {
        return (
            <div
                style={{
                    position: "fixed",
                    inset: 0,
                    backgroundColor: OVERLAY_COLOR,
                    zIndex: 10000,
                }}
            />
        );
    }

    // Spotlight overlay — box-shadow creates the dark surround, the div itself is the transparent hole
    return (
        <>
            {/* Click-catching overlay with a hole cut out for the spotlight area.
          Clicks on the dark surround trigger cancel; clicks inside the hole pass through to the target. */}
            {onOverlayClick && (
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- modal backdrop overlay; click-outside-to-dismiss is not a button interaction
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 9999,
                        clipPath: `polygon(evenodd, 0% 0%, 100% 0%, 100% 100%, 0% 100%, 0% 0%, ${clampedLeft}px ${clampedTop}px, ${clampedLeft}px ${clampedBottom}px, ${clampedRight}px ${clampedBottom}px, ${clampedRight}px ${clampedTop}px, ${clampedLeft}px ${clampedTop}px)`,
                    }}
                    onClick={onOverlayClick}
                />
            )}
            <div
                style={{
                    position: "fixed",
                    left: clampedLeft,
                    top: clampedTop,
                    width: clampedWidth,
                    height: clampedHeight,
                    borderRadius: 8,
                    border: "2px solid #2EDEE5",
                    boxShadow: isAction ? undefined : `0 0 0 9999px ${OVERLAY_COLOR}, 0 0 20px rgba(46, 222, 229, 0.3)`,
                    animation: isAction ? "tour-spotlight-pulse 2s ease-in-out infinite" : undefined,
                    zIndex: 10000,
                    pointerEvents: spotlightClicks ? "none" : "auto",
                    transition: "left 0.2s ease, top 0.2s ease, width 0.2s ease, height 0.2s ease",
                }}
            />
        </>
    );
}

type TourStepData = {
    scoutState: ScoutState;
    totalSteps: number;
    actionLabel?: string;
    endsTour?: boolean;
    isInGroup?: boolean;
};

type GroupStackEntry = {
    returnSteps: TourStep[];
    returnIndex: number;
};

export default function TourEngine() {
    const {
        activeTour,
        stepIndex,
        run,
        setStepIndex,
        stopTour,
        pauseTour,
        resumeTour,
        markCompleted,
        markDismissed,
        registerAdvanceStep,
    } = useTour();
    const navigate = useNavigate();
    const location = useLocation();
    const locationRef = useRef(location);
    locationRef.current = location;
    const activeTourRef = useRef(activeTour);
    activeTourRef.current = activeTour;
    const stepIndexRef = useRef(stepIndex);
    stepIndexRef.current = stepIndex;
    const observerRef = useRef<MutationObserver | null>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [measureKey, setMeasureKey] = useState(0);
    const activeTourId = activeTour?.id ?? null;
    /** True while a MutationObserver is waiting for a target to appear. */
    const [isWaiting, setIsWaiting] = useState(false);
    /** True when a target wasn't found — renders the current step as a center modal instead of skipping. */
    const [fallbackCenter, setFallbackCenter] = useState(false);
    /** Whether the Next button should be disabled (enableNextSelector not matched). */
    const [disableNext, setDisableNext] = useState(false);
    /** Whether the cancel confirmation modal is open. */
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);

    // --- Group stack for branching ---
    const [activeSteps, setActiveSteps] = useState<TourStep[]>([]);
    const [groupStack, setGroupStack] = useState<GroupStackEntry[]>([]);
    const activeStepsRef = useRef<TourStep[]>([]);
    const groupStackRef = useRef<GroupStackEntry[]>([]);

    // Reset active steps when tour changes
    useEffect(() => {
        if (activeTour) {
            setActiveSteps(activeTour.steps);
            activeStepsRef.current = activeTour.steps;
        } else {
            setActiveSteps([]);
            activeStepsRef.current = [];
        }
        setGroupStack([]);
        groupStackRef.current = [];
        setFallbackCenter(false);
        setIsWaiting(false);
        navAttemptRef.current = null;
    }, [activeTourId]);

    /** Stop the MutationObserver and clear any pending waitForTarget timeout. */
    const cleanupWait = useCallback(() => {
        observerRef.current?.disconnect();
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
    }, []);

    // Clean up MutationObserver + timeout when the tour ends.
    // Note: do NOT include stepIndex — advanceToStep calls setStepIndex before waitForTarget,
    // and the cleanup effect would disconnect the observer we just set up.
    useEffect(() => {
        return cleanupWait;
    }, [activeTourId, cleanupWait]);

    // Lock page scroll while tour is active so the spotlight overlay stays aligned with its target
    useEffect(() => {
        if (!activeTour) return;
        const html = document.documentElement;
        const prev = html.style.overflow;
        html.style.overflow = "hidden";
        return () => {
            html.style.overflow = prev;
        };
    }, [activeTour]);

    // Inject pulse keyframes when a tour is active, clean up on deactivation
    useEffect(() => {
        if (!activeTour) return;
        const id = "tour-pulse-keyframes";
        if (document.getElementById(id)) return;
        const style = document.createElement("style");
        style.id = id;
        style.textContent = TOUR_KEYFRAMES;
        document.head.appendChild(style);
        return () => {
            style.remove();
        };
    }, [activeTour]);

    const joyrideSteps = useMemo(() => {
        if (!activeTour || activeSteps.length === 0) return [];
        return activeSteps.map((step, i) => {
            // When a target wasn't found, render the current step as a center modal
            const isFallback = fallbackCenter && i === stepIndex;
            return {
                target: isFallback ? "body" : stepTarget(step),
                title: step.title,
                content: step.content,
                placement: isFallback ? ("center" as const) : step.placement,
                disableBeacon: true,
                disableOverlay: true,
                disableScrolling: true,
                spotlightClicks: false,
                data: {
                    scoutState: step.scoutState,
                    totalSteps: activeTour.steps.length,
                    actionLabel: !isFallback && isTargetedStep(step) ? step.actionLabel : undefined,
                    endsTour: step.endsTour,
                    isInGroup: groupStack.length > 0,
                },
            };
        });
    }, [activeTour, activeSteps, fallbackCenter, stepIndex, groupStack]);

    // Navigate if the current step requires a different route.
    // React Router's navigate() synchronously updates the history entry, so the next render
    // will see the new location. If a route guard redirects elsewhere, this effect re-fires
    // and logs a warning after a failed attempt.
    const navAttemptRef = useRef<{ route: string; stepIndex: number } | null>(null);
    useEffect(() => {
        if (!activeTour || !run || activeSteps.length === 0) return;
        const currentStep = activeSteps[stepIndex];
        if (!currentStep?.route) {
            navAttemptRef.current = null;
            return;
        }
        if (!isOnRoute(location, currentStep.route)) {
            const prev = navAttemptRef.current;
            if (prev && prev.route === currentStep.route && prev.stepIndex === stepIndex) {
                // We already tried to navigate to this route but ended up elsewhere (guard redirect?)
                console.warn(`[Tours] Navigation to "${currentStep.route}" was redirected — stopping tour`);
                Sentry.captureMessage("Tour navigation redirected", {
                    level: "warning",
                    extra: {
                        tourId: activeTour.id,
                        stepIndex,
                        expectedRoute: currentStep.route,
                        actualPath: location.pathname,
                    },
                });
                navAttemptRef.current = null;
                stopTour();
                return;
            }
            navAttemptRef.current = { route: currentStep.route, stepIndex };
            navigate(currentStep.route);
        } else {
            navAttemptRef.current = null;
        }
    }, [stepIndex, activeSteps, activeTour, run, location.pathname, location.search, navigate, stopTour]);

    /** Branch into the current step's onTimeoutGroup, or show as a center modal if no group is configured. Called on waitForTarget timeout or early bailout. */
    const enterGroupOrFallback = useCallback(
        (reason: string) => {
            cleanupWait();

            const tour = activeTourRef.current;
            const idx = stepIndexRef.current;
            const step = activeStepsRef.current[idx];
            const msg = `[Tours] ${reason} in tour "${tour?.id}" at step ${idx}`;
            console.warn(msg);
            Sentry.captureMessage(msg, { level: "warning", extra: { tourId: tour?.id, stepIndex: idx } });

            setIsWaiting(false);
            setFallbackCenter(false);

            // Check if this step has an onTimeoutGroup to branch into
            if (step && isTargetedStep(step) && step.onTimeoutGroup && tour?.groups?.[step.onTimeoutGroup]) {
                const groupName = step.onTimeoutGroup;
                const groupSteps = tour.groups[groupName];
                // Guard against empty group arrays (validated in dev, but defend in production)
                if (!groupSteps || groupSteps.length === 0) {
                    const emptyMsg = `[Tours] Group "${groupName}" in tour "${tour?.id}" is empty — cannot enter`;
                    console.error(emptyMsg);
                    Sentry.captureMessage(emptyMsg, { level: "error", extra: { tourId: tour?.id, groupName } });
                    setFallbackCenter(true);
                    resumeTour();
                    return;
                }
                // Push current context onto group stack
                const returnSteps = activeStepsRef.current;
                const returnIndex = idx + 1;
                const newStack = [...groupStackRef.current, { returnSteps, returnIndex }];
                groupStackRef.current = newStack;
                setGroupStack(newStack);
                // Enter the group
                activeStepsRef.current = groupSteps;
                setActiveSteps(groupSteps);
                setStepIndex(0);
                resumeTour();
            } else {
                // No group — show as center fallback
                setFallbackCenter(true);
                resumeTour();
            }
        },
        [cleanupWait, resumeTour, setStepIndex],
    );

    // Ref for advanceToStep — declared early so waitForTarget can call it without circular deps.
    // Updated after advanceToStep is defined below.
    const advanceToStepRef = useRef<(nextIndex: number) => void>(() => {});

    const waitForTarget = useCallback(
        (selector: string, timeoutMs = 5000, stepOverride?: TourStep) => {
            cleanupWait();

            // Element already exists — resume immediately
            if (document.querySelector(selector)) {
                setIsWaiting(false);
                setFallbackCenter(false);
                setMeasureKey((k) => k + 1);
                resumeTour();
                return;
            }

            // Use the passed step (avoids stale stepIndexRef after setStepIndex), fall back to ref lookup
            const step = stepOverride ?? activeStepsRef.current[stepIndexRef.current];
            const bailout = step && isTargetedStep(step) ? step.bailoutSelector : undefined;
            const advanceSel = step && isTargetedStep(step) ? step.advanceWhenSelector : undefined;

            // Check bailoutSelector before waiting — if the bailout condition is already met, enter group immediately
            if (bailout && document.querySelector(bailout)) {
                enterGroupOrFallback(`Bailout selector "${bailout}" found (target "${selector}" not present)`);
                return;
            }

            // Check advanceWhenSelector before waiting — if already present, skip ahead immediately
            if (advanceSel && document.querySelector(advanceSel)) {
                cleanupWait();
                setIsWaiting(false);
                setFallbackCenter(false);
                advanceToStepRef.current(stepIndexRef.current + 1);
                return;
            }

            setIsWaiting(true);
            setFallbackCenter(false);
            pauseTour();

            observerRef.current = new MutationObserver(() => {
                try {
                    // Target appeared — success
                    if (document.querySelector(selector)) {
                        cleanupWait();
                        setIsWaiting(false);
                        setFallbackCenter(false);
                        setMeasureKey((k) => k + 1);
                        resumeTour();
                        return;
                    }
                    // Bailout selector appeared — early exit into group
                    if (bailout && document.querySelector(bailout)) {
                        cleanupWait();
                        enterGroupOrFallback(`Bailout selector "${bailout}" found while waiting for "${selector}"`);
                        return;
                    }
                    // advanceWhenSelector — auto-advance when a different element appears
                    if (advanceSel && document.querySelector(advanceSel)) {
                        cleanupWait();
                        setIsWaiting(false);
                        setFallbackCenter(false);
                        advanceToStepRef.current(stepIndexRef.current + 1);
                        return;
                    }
                } catch (e) {
                    // Invalid CSS selector or unexpected DOM error — bail out immediately instead of waiting for timeout
                    console.error("[Tours] MutationObserver callback error:", e);
                    Sentry.captureException(e, { extra: { selector, bailout } });
                    enterGroupOrFallback(`MutationObserver error for selector "${selector}"`);
                }
            });

            observerRef.current.observe(document.body, {
                childList: true,
                subtree: true,
                attributes: true,
            });

            timeoutRef.current = setTimeout(() => {
                enterGroupOrFallback(`Target "${selector}" not found after ${timeoutMs / 1000}s`);
            }, timeoutMs);
        },
        [cleanupWait, pauseTour, resumeTour, enterGroupOrFallback],
    );

    /** Shared logic for advancing to the next step index — handles completion, navigation, groups, and waitForTarget. */
    const advanceToStep = useCallback(
        (nextIndex: number) => {
            if (!activeTour) return;
            cleanupWait();
            setFallbackCenter(false);
            setIsWaiting(false);

            // Check bounds — pop group stack iteratively until we find a valid index or exhaust the stack
            let iterations = 0;
            while (nextIndex >= activeStepsRef.current.length) {
                if (++iterations > 20) {
                    const msg = `[Tours] advanceToStep exceeded 20 stack-pop iterations in tour "${activeTour.id}" — stopping`;
                    console.error(msg);
                    Sentry.captureMessage(msg, { level: "error", extra: { tourId: activeTour.id, iterations } });
                    stopTour();
                    return;
                }
                if (groupStackRef.current.length === 0) {
                    markCompleted(activeTour.id, activeStepsRef.current.length);
                    stopTour();
                    return;
                }
                const stack = [...groupStackRef.current];
                const entry = stack.pop()!;
                groupStackRef.current = stack;
                setGroupStack(stack);
                activeStepsRef.current = entry.returnSteps;
                setActiveSteps(entry.returnSteps);
                nextIndex = entry.returnIndex;
            }

            const nextStep = activeStepsRef.current[nextIndex];

            // Navigate if the next step requires a different route
            if (nextStep.route && !isOnRoute(locationRef.current, nextStep.route)) {
                setStepIndex(nextIndex);
                navigate(nextStep.route);
                if (isTargetedStep(nextStep)) {
                    waitForTarget(nextStep.target, nextStep.waitForTargetTimeout, nextStep);
                }
                return;
            }

            // Wait for target if it doesn't exist yet
            if (
                isTargetedStep(nextStep) &&
                (nextStep.waitForTarget || nextStep.route) &&
                !document.querySelector(nextStep.target)
            ) {
                setStepIndex(nextIndex);
                waitForTarget(nextStep.target, nextStep.waitForTargetTimeout, nextStep);
                return;
            }

            setStepIndex(nextIndex);
        },
        [activeTour, cleanupWait, markCompleted, stopTour, setStepIndex, waitForTarget, navigate],
    );

    // Keep the ref current so waitForTarget and external callers always use the latest advanceToStep
    advanceToStepRef.current = advanceToStep;
    useEffect(() => {
        registerAdvanceStep(() => advanceToStepRef.current(stepIndexRef.current + 1));
        return () => registerAdvanceStep(null);
    }, [registerAdvanceStep]);

    const handleCallback = useCallback(
        (data: CallBackProps) => {
            const { action, index, status, type } = data;

            // Use ref instead of closure to avoid stale-closure race: if Joyride fires a
            // pending callback after stopTour() but before React re-renders with the new
            // handleCallback, the closure's activeTour would still be non-null.
            const tour = activeTourRef.current;
            if (!tour) return;

            // FINISHED is handled by advanceToStep when it reaches end of steps.
            // Ignore Joyride's FINISHED to avoid double-completing during group transitions.
            if (status === STATUS.FINISHED) {
                return;
            }

            if (status === STATUS.SKIPPED) {
                // Use `index` from callback data (not `stepIndex` state) to avoid stale closure
                markDismissed(tour.id, index);
                stopTour();
                return;
            }

            if (type === EVENTS.STEP_AFTER) {
                // If the current step ends the tour, stop without advancing
                const stepDef = activeStepsRef.current[index];
                if (stepDef?.endsTour && action !== ACTIONS.PREV) {
                    markDismissed(tour.id, index);
                    stopTour();
                    return;
                }
                const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);
                if (nextIndex >= 0) {
                    advanceToStep(nextIndex);
                }
                return;
            }

            if (type === EVENTS.TARGET_NOT_FOUND) {
                const currentStep = activeStepsRef.current[index];
                if (!currentStep || !isTargetedStep(currentStep)) return;
                const msg = `[Tours] Target "${currentStep.target}" not found in tour "${tour.id}" at step ${index}`;
                console.warn(msg);
                Sentry.captureMessage(msg, {
                    level: "warning",
                    extra: { tourId: tour.id, stepIndex: index, target: currentStep.target },
                });
                if (currentStep.waitForTarget || currentStep.route) {
                    waitForTarget(currentStep.target, currentStep.waitForTargetTimeout, currentStep);
                } else {
                    // Show the step as a center modal instead of skipping it entirely
                    setFallbackCenter(true);
                }
            }
        },
        [markDismissed, stopTour, waitForTarget, advanceToStep],
    );

    const handleOverlayClick = useCallback(() => {
        setShowCancelConfirm(true);
    }, []);

    const handleCancelConfirm = useCallback(() => {
        setShowCancelConfirm(false);
        if (activeTour) {
            markDismissed(activeTour.id, stepIndex);
            stopTour();
        }
    }, [activeTour, stepIndex, markDismissed, stopTour]);

    const handleCancelCancel = useCallback(() => {
        setShowCancelConfirm(false);
    }, []);

    const TooltipWrapper = useCallback(
        (props: TooltipRenderProps) => {
            const stepData = props.step.data as TourStepData | undefined;
            return (
                <ScoutTooltip
                    {...props}
                    scoutState={stepData?.scoutState ?? "idle"}
                    totalSteps={stepData?.totalSteps ?? 0}
                    actionLabel={stepData?.actionLabel}
                    endsTour={stepData?.endsTour}
                    isInGroup={stepData?.isInGroup}
                    onRequestCancel={handleOverlayClick}
                    disableNext={disableNext}
                />
            );
        },
        [handleOverlayClick, disableNext],
    );

    // Auto-advance: when user clicks an action step's target element, advance the tour
    useEffect(() => {
        if (!activeTour || !run || activeSteps.length === 0) return;
        const currentStep = activeSteps[stepIndex];
        if (!currentStep || !isTargetedStep(currentStep) || !currentStep.actionLabel) return;

        const el = document.querySelector(currentStep.target);
        if (!el) return;

        const handleClick = () => advanceToStep(stepIndex + 1);

        el.addEventListener("click", handleClick);
        return () => el.removeEventListener("click", handleClick);
    }, [activeTour, activeSteps, stepIndex, run, advanceToStep]);

    // advanceWhenSelector: watch when the step is visible (run=true, not waiting) for a selector to appear
    const advanceWhenObserverRef = useRef<MutationObserver | null>(null);
    useEffect(() => {
        advanceWhenObserverRef.current?.disconnect();
        advanceWhenObserverRef.current = null;

        if (!activeTour || !run || activeSteps.length === 0) return;
        const currentStep = activeSteps[stepIndex];
        if (!currentStep || !isTargetedStep(currentStep) || !currentStep.advanceWhenSelector) return;

        const selector = currentStep.advanceWhenSelector;

        // If already exists, advance immediately
        if (document.querySelector(selector)) {
            advanceToStep(stepIndex + 1);
            return;
        }

        const observer = new MutationObserver(() => {
            if (document.querySelector(selector)) {
                observer.disconnect();
                advanceWhenObserverRef.current = null;
                advanceToStep(stepIndex + 1);
            }
        });

        observer.observe(document.body, { childList: true, subtree: true });
        advanceWhenObserverRef.current = observer;

        return () => {
            observer.disconnect();
            advanceWhenObserverRef.current = null;
        };
    }, [activeTour, activeSteps, stepIndex, run, advanceToStep]);

    // enableNextSelector: poll to reactively disable/enable the Next button
    useEffect(() => {
        if (!activeTour || !run || activeSteps.length === 0) {
            setDisableNext(false);
            return;
        }
        const currentStep = activeSteps[stepIndex];
        const selector = currentStep && isTargetedStep(currentStep) ? currentStep.enableNextSelector : undefined;
        if (!selector) {
            setDisableNext(false);
            return;
        }
        // Check immediately, then poll
        setDisableNext(!document.querySelector(selector));
        const poll = setInterval(() => {
            setDisableNext(!document.querySelector(selector));
        }, 300);
        return () => clearInterval(poll);
    }, [activeTour, activeSteps, stepIndex, run]);

    if (!activeTour || activeSteps.length === 0) return null;

    const currentStep = activeSteps[stepIndex];
    if (!currentStep) {
        // During group transitions, setActiveSteps and setStepIndex are batched but may render
        // in an intermediate state where stepIndex exceeds the stale activeSteps array.
        // Check the ref (which is updated synchronously) before treating this as a real error.
        if (activeStepsRef.current[stepIndex]) {
            // Ref has the correct steps — this is a transient render; skip rendering until state catches up
            return null;
        }
        const msg = `[Tours] stepIndex ${stepIndex} out of bounds for tour "${activeTour.id}" (${activeSteps.length} steps)`;
        console.warn(msg);
        Sentry.captureMessage(msg, {
            level: "error",
            extra: { tourId: activeTour.id, stepIndex, totalSteps: activeSteps.length },
        });
        stopTour();
        return null;
    }
    const isCenter = currentStep.placement === "center" || fallbackCenter;
    const isAction = !fallbackCenter && isTargetedStep(currentStep) && !!currentStep.actionLabel;

    return (
        <>
            <TourOverlay
                target={fallbackCenter ? "body" : stepTarget(currentStep)}
                isCenter={isCenter}
                spotlightClicks={!fallbackCenter && isTargetedStep(currentStep) && !!currentStep.spotlightClicks}
                isAction={isAction}
                measureKey={measureKey}
                disableAutoScroll={isTargetedStep(currentStep) ? currentStep.disableAutoScroll : undefined}
                isWaiting={isWaiting}
                onOverlayClick={isWaiting ? undefined : handleOverlayClick}
            />
            <ConfirmModal
                id="tour-cancel-confirm"
                isOpen={showCancelConfirm}
                headerText="End tour?"
                contentText="Are you sure you want to end the tour? You can always restart it from the Help menu."
                confirmButtonText="End tour"
                confirmButtonColor="bg-red-600"
                confirmHoverButtonColor="bg-red-500"
                cancelButtonText="Continue Tour"
                onConfirm={handleCancelConfirm}
                onCancel={handleCancelCancel}
                customZIndex={10111}
            />
            {run && !showCancelConfirm && (
                <Joyride
                    steps={joyrideSteps}
                    stepIndex={stepIndex}
                    run={run}
                    continuous
                    showSkipButton
                    disableOverlayClose
                    tooltipComponent={TooltipWrapper}
                    callback={handleCallback}
                    styles={{
                        options: {
                            zIndex: 10001,
                            arrowColor: "transparent",
                        },
                    }}
                    floaterProps={{
                        disableAnimation: true,
                    }}
                />
            )}
        </>
    );
}
